import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  JURISDICTION_CODES,
  JURISDICTIONS,
  SUPPORTED_CURRENCIES,
  goalMetricAllowed,
  type CreateGoalInput,
  type Currency,
  type GoalOutput,
  type GoalProgressRow,
  type GoalScopeType,
  type UpdateGoalInput,
  type UserOutput,
} from '@1financial/shared';
import { useAuth, getAccessToken, authenticatedFetch as fetch } from '../lib/auth-context.js';
import { httpError } from '../lib/http-error.js';
import { useCapabilities } from '../hooks/useCapabilities.js';
import { useComplianceServiceTypes } from '../hooks/useComplianceServiceTypes.js';
import { SearchableSelect } from '../components/SearchableSelect.js';
import { TableSkeletonRows } from '../components/Skeleton.js';
import { EmptyState } from '../components/EmptyState.js';
import { EditIcon, DeleteIcon } from '../components/ActionIcons.js';
import { Target } from 'lucide-react';
import { fmtDate, fmtDateTime } from '../lib/date.js';
import { DateInput } from '../components/DateInput.js';
import { useUserPreference } from '../lib/user-preferences.js';

type ModalMode = { kind: 'new' } | { kind: 'edit'; goal: GoalOutput } | null;
interface ScopeOption { key: GoalScopeType; label: string; enabled: boolean; sortOrder: number }
interface MetricOption { scopeType: GoalScopeType; key: GoalOutput['metric']; label: string; enabled: boolean; sortOrder: number }

const METRIC_LABELS: Record<GoalOutput['metric'], string> = {
  HOURS_BILLED: 'Hours billed',
  LEADS_ACQUIRED: 'Leads acquired',
  ITEMS_COMPLETED: 'Compliance items completed',
  ENGAGEMENT_STEPS_COMPLETED: 'Engagement steps completed',
  ENGAGEMENTS_WON: 'Engagements won',
  ENGAGEMENTS_COMPLETED: 'Engagements completed',
  PROPOSALS_SENT: 'Proposals sent',
  PROPOSALS_ACCEPTED: 'Proposals accepted',
  REVENUE: 'Revenue collected',
  CUSTOM: 'Custom / manual',
};

const PERIOD_LABELS: Record<GoalOutput['period'], string> = {
  WEEKLY: 'Weekly', MONTHLY: 'Monthly', QUARTERLY: 'Quarterly', ANNUAL: 'Annual',
};

const SCOPE_LABELS: Record<GoalScopeType, string> = {
  FIRM: 'All work',
  JURISDICTION: 'Jurisdiction',
  ENGAGEMENT: 'Engagement',
  COMPLIANCE_SERVICE: 'Compliance',
  PROPOSAL: 'Proposal',
};

function jurisdictionLabel(code: string | null) {
  return JURISDICTIONS.find((j) => j.code === code)?.label ?? code ?? '—';
}

function scopeLabel(goal: GoalOutput) {
  if (goal.scopeType === 'FIRM') return 'All jurisdictions';
  if (goal.scopeType === 'PROPOSAL') return 'All proposals';
  if (goal.scopeType === 'ENGAGEMENT') return 'All engagements';
  if (goal.scopeType === 'JURISDICTION') return jurisdictionLabel(goal.jurisdiction);
  return `${jurisdictionLabel(goal.jurisdiction)} · ${goal.serviceType}`;
}

export function GoalsPage() {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [modal, setModal] = useState<ModalMode>(null);
  const [deleting, setDeleting] = useState<GoalOutput | null>(null);
  const [details, setDetails] = useState<GoalOutput | null>(null);
  const [filters, setFilters] = useUserPreference('goals.filters', { scopeType: '', period: '', owner: 'all', status: 'active' }, {
    validate: (value) => !!value && typeof value === 'object'
      && ['scopeType', 'period', 'owner', 'status'].every((key) => typeof (value as Record<string, unknown>)[key] === 'string'),
  });
  const { canDo } = useCapabilities();
  const canManage = canDo('goals.manage');
  const canDeleteAny = canDo('goals.delete');

  const catalog = useQuery({
    queryKey: ['goal-catalog'],
    queryFn: async () => {
      const res = await fetch('/v1/goals/catalog', { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: { scopes: ScopeOption[]; metrics: MetricOption[] } };
      return body.data;
    },
  });
  const scopeOptions = (catalog.data?.scopes ?? []).filter((scope) => scope.enabled).sort((a, b) => a.sortOrder - b.sortOrder);
  const metricLabel = (metric: GoalOutput['metric']) => catalog.data?.metrics.find((item) => item.key === metric)?.label ?? METRIC_LABELS[metric];
  const scopeTypeLabel = (scopeType: GoalScopeType) => catalog.data?.scopes.find((item) => item.key === scopeType)?.label ?? SCOPE_LABELS[scopeType];

  const list = useQuery({
    queryKey: ['goals', filters.scopeType, filters.period, filters.owner],
    queryFn: async () => {
      const p = new URLSearchParams({ limit: '100' });
      if (filters.scopeType) p.set('scopeType', filters.scopeType);
      if (filters.period) p.set('period', filters.period);
      if (filters.owner === 'firm') p.set('firmWide', 'true');
      else if (filters.owner !== 'all') p.set('ownerId', filters.owner);
      const res = await fetch(`/v1/goals?${p}`, { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: { items: GoalOutput[] } };
      return body.data.items;
    },
  });

  const users = useQuery({
    queryKey: ['users', { limit: 100 }],
    enabled: canManage,
    queryFn: async () => {
      const res = await fetch('/v1/users?limit=100', { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw new Error(`Failed: ${res.statusText}`);
      const body = await res.json() as { ok: true; data: { items: UserOutput[] } };
      return body.data.items;
    },
  });

  const create = useMutation({
    mutationFn: async (input: CreateGoalInput) => {
      const res = await fetch('/v1/goals', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` }, body: JSON.stringify(input),
      });
      if (!res.ok) throw await httpError(res);
      return res.json();
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['goals'] }); setModal(null); },
  });

  const update = useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateGoalInput }) => {
      const res = await fetch(`/v1/goals/${id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` }, body: JSON.stringify(input),
      });
      if (!res.ok) throw await httpError(res);
      return res.json();
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['goals'] }); setModal(null); },
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/v1/goals/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw await httpError(res);
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['goals'] }); setDeleting(null); },
  });

  function ownerName(goal: GoalOutput) {
    if (goal.firmWide) return 'Whole team';
    if (goal.ownerId === currentUser?.id) return currentUser.displayName ?? 'Me';
    return users.data?.find((x) => x.id === goal.ownerId)?.displayName ?? goal.ownerId?.slice(0, 8) ?? '—';
  }

  const visibleGoals = (list.data ?? []).filter((goal) => {
    const done = goal.currentValue >= goal.targetValue;
    return filters.status === 'all' || (filters.status === 'completed' ? done : !done);
  });

  return (
    <div className="space-y-4">
      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-white">Goals</h1>
          <p className="text-sm text-muted">Scoped targets · progress rolls up automatically every hour</p>
        </div>
        <button onClick={() => setModal({ kind: 'new' })} className="bg-accent hover:bg-emerald-400 text-accent-fg text-sm font-medium px-3 py-1.5 rounded-lg">+ New goal</button>
      </header>

      <div className="bg-bg-panel border border-border rounded-xl p-3 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2">
        <select aria-label="Filter scope" value={filters.scopeType} onChange={(e) => setFilters((f) => ({ ...f, scopeType: e.target.value }))} className="bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white">
          <option value="">All scopes</option>
          {scopeOptions.map((scope) => <option key={scope.key} value={scope.key}>{scope.label}</option>)}
        </select>
        <select aria-label="Filter period" value={filters.period} onChange={(e) => setFilters((f) => ({ ...f, period: e.target.value }))} className="bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white">
          <option value="">All periods</option>
          {Object.entries(PERIOD_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
        {canManage ? (
          <select aria-label="Filter owner" value={filters.owner} onChange={(e) => setFilters((f) => ({ ...f, owner: e.target.value }))} className="bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white">
            <option value="all">All owners</option><option value="firm">Whole team</option>
            {(users.data ?? []).filter((u) => u.status !== 'DISABLED').map((u) => <option key={u.id} value={u.id}>{u.displayName}</option>)}
          </select>
        ) : <div className="hidden xl:block" />}
        <select aria-label="Filter status" value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} className="bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white">
          <option value="active">Active / not reached</option><option value="completed">Target reached</option><option value="all">All statuses</option>
        </select>
      </div>

      <div className="bg-bg-panel border border-border rounded-xl overflow-hidden overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]">
          <thead><tr className="text-left text-xs text-muted bg-bg-card border-b border-border">
            <th className="px-4 py-3 font-medium">Goal</th><th className="px-4 py-3 font-medium">Scope</th><th className="px-4 py-3 font-medium">Owner</th><th className="px-4 py-3 font-medium">Period</th><th className="px-4 py-3 font-medium w-64">Progress</th><th className="px-4 py-3 font-medium text-right">Actions</th>
          </tr></thead>
          <tbody>
            {list.isLoading && <TableSkeletonRows colSpan={6} />}
            {list.error && <tr><td colSpan={6} className="px-4 py-8 text-center text-rose-400">{(list.error as Error).message}</td></tr>}
            {!list.isLoading && !list.error && visibleGoals.length === 0 && <tr><td colSpan={6}><EmptyState icon={Target} title="No goals match these filters." /></td></tr>}
            {visibleGoals.map((g) => {
              const pct = g.targetValue > 0 ? (g.currentValue / g.targetValue) * 100 : 0;
              const done = pct >= 100;
              const expired = new Date(g.endDate) < new Date();
              const canViewDetails = g.metric !== 'CUSTOM' && (!g.firmWide || canManage);
              return (
                <tr key={g.id} className="border-b border-border/50 hover:bg-bg-hover/40">
                  <td className="px-4 py-3"><div className="text-white">{g.title}</div><div className="text-xs text-muted">{metricLabel(g.metric)}{g.metric === 'REVENUE' && g.currency ? ` · ${g.currency}` : ''}</div></td>
                  <td className="px-4 py-3 text-muted max-w-56"><div className="truncate" title={scopeLabel(g)}>{scopeLabel(g)}</div><div className="text-[11px]">{scopeTypeLabel(g.scopeType)}</div></td>
                  <td className="px-4 py-3 text-muted">{ownerName(g)}</td>
                  <td className="px-4 py-3 text-muted">{PERIOD_LABELS[g.period]}<span className={expired && !done ? ' text-rose-400' : ''}> · ends {fmtDate(g.endDate)}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 rounded-full bg-bg-hover overflow-hidden"><div className={`h-full rounded-full ${done ? 'bg-emerald-400' : 'bg-accent'}`} style={{ width: `${Math.min(100, pct)}%` }} /></div>
                      {canViewDetails ? (
                        <button onClick={() => setDetails(g)} className={`text-xs whitespace-nowrap hover:text-accent ${done ? 'text-emerald-300' : 'text-muted'}`} title="View contributing records">
                          {fmtValue(g.currentValue, g.metric, g.currency)} / {fmtValue(g.targetValue, g.metric, g.currency)}
                        </button>
                      ) : <span className={`text-xs whitespace-nowrap ${done ? 'text-emerald-300' : 'text-muted'}`}>{fmtValue(g.currentValue, g.metric, g.currency)} / {fmtValue(g.targetValue, g.metric, g.currency)}</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {((!g.firmWide && g.ownerId === currentUser?.id) || canManage) && <EditIcon onClick={() => setModal({ kind: 'edit', goal: g })} label="Edit goal" />}
                    {((!g.firmWide && g.ownerId === currentUser?.id) || canDeleteAny) && <DeleteIcon onClick={() => setDeleting(g)} label="Delete goal" />}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {modal && <GoalModal mode={modal} users={users.data ?? []} scopes={catalog.data?.scopes ?? []} metrics={catalog.data?.metrics ?? []} canManage={canManage} onClose={() => setModal(null)} onCreate={(input) => create.mutate(input)} onUpdate={(id, input) => update.mutate({ id, input })} isPending={create.isPending || update.isPending} error={(create.error?.message ?? update.error?.message) || null} />}
      {deleting && <DeleteModal goal={deleting} onClose={() => setDeleting(null)} onConfirm={() => remove.mutate(deleting.id)} isPending={remove.isPending} error={remove.error?.message ?? null} />}
      {details && <GoalProgressModal goal={details} scope={scopeLabel(details)} metric={metricLabel(details.metric)} onClose={() => setDetails(null)} />}
    </div>
  );
}

function GoalModal({ mode, users, scopes, metrics, canManage, onClose, onCreate, onUpdate, isPending, error }: {
  mode: { kind: 'new' } | { kind: 'edit'; goal: GoalOutput };
  users: UserOutput[];
  scopes: ScopeOption[];
  metrics: MetricOption[];
  canManage: boolean;
  onClose: () => void;
  onCreate: (input: CreateGoalInput) => void;
  onUpdate: (id: string, input: UpdateGoalInput) => void;
  isPending: boolean;
  error: string | null;
}) {
  const isEdit = mode.kind === 'edit';
  const goal = isEdit ? mode.goal : null;
  const { user: currentUser } = useAuth();
  const [form, setForm] = useState({
    title: goal?.title ?? '',
    scopeType: goal?.scopeType ?? 'COMPLIANCE_SERVICE' as GoalScopeType,
    jurisdiction: goal?.jurisdiction ?? '',
    serviceType: goal?.serviceType ?? '',
    metric: (goal && !goalMetricAllowed(goal.metric, goal.scopeType) ? 'ITEMS_COMPLETED' : goal?.metric ?? 'ITEMS_COMPLETED') as GoalOutput['metric'],
    currency: goal?.currency ?? 'AED' as Currency,
    targetValue: goal?.targetValue ?? 0,
    currentValue: goal?.currentValue ?? 0,
    period: goal?.period ?? 'MONTHLY' as GoalOutput['period'],
    startDate: goal ? goal.startDate.slice(0, 10) : new Date().toISOString().slice(0, 10),
    endDate: goal ? goal.endDate.slice(0, 10) : new Date().toISOString().slice(0, 10),
    notes: goal?.notes ?? '',
    firmWide: goal?.firmWide ?? false,
    ownerId: goal?.ownerId ?? currentUser?.id ?? '',
  });

  const { serviceTypes, isLoading: serviceTypesLoading } = useComplianceServiceTypes(form.jurisdiction);
  const showLegacyService = form.serviceType !== '' && form.serviceType !== 'Other' && !serviceTypes.includes(form.serviceType);
  const enabledScopes = scopes.filter((scope) => scope.enabled).sort((a, b) => a.sortOrder - b.sortOrder);

  function updateForm<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function changeScope(scopeType: GoalScopeType) {
    const firstMetric = metrics.filter((metric) => metric.scopeType === scopeType && metric.enabled && goalMetricAllowed(metric.key, scopeType)).sort((a, b) => a.sortOrder - b.sortOrder)[0]?.key ?? 'ITEMS_COMPLETED';
    setForm((current) => ({
      ...current,
      scopeType,
      serviceType: scopeType === 'COMPLIANCE_SERVICE' ? current.serviceType : '',
      metric: metrics.some((metric) => metric.scopeType === scopeType && metric.key === current.metric && metric.enabled) ? current.metric : firstMetric,
    }));
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const base = {
      scopeType: form.scopeType,
      jurisdiction: form.scopeType === 'JURISDICTION' || form.scopeType === 'COMPLIANCE_SERVICE' ? form.jurisdiction : null,
      engagementId: null,
      serviceType: form.scopeType === 'COMPLIANCE_SERVICE' ? form.serviceType : null,
      title: form.title,
      metric: form.metric,
      ...(form.metric === 'REVENUE' ? { currency: form.currency } : {}),
      targetValue: Number(form.targetValue),
      period: form.period,
      startDate: `${form.startDate}T00:00:00.000Z`,
      endDate: `${form.endDate}T00:00:00.000Z`,
      notes: form.notes || null,
    };
    if (isEdit) onUpdate(goal!.id, { ...base, ...(form.metric === 'CUSTOM' ? { currentValue: Number(form.currentValue) } : {}) });
    else onCreate({ ...base, firmWide: form.firmWide, ...(form.firmWide ? {} : { ownerId: form.ownerId || undefined }) });
  }

  return (
    <ModalShell title={isEdit ? 'Edit goal' : 'New goal'} onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-3">
        <div><label className="block text-xs text-muted mb-1">Title <span className="text-rose-400">*</span></label><input value={form.title} onChange={(e) => updateForm('title', e.target.value)} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white" maxLength={200} required /></div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><label className="block text-xs text-muted mb-1">Scope <span className="text-rose-400">*</span></label><select value={form.scopeType} onChange={(e) => changeScope(e.target.value as GoalScopeType)} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white">{enabledScopes.map((scope) => <option key={scope.key} value={scope.key}>{scope.label}</option>)}{goal && !enabledScopes.some((scope) => scope.key === form.scopeType) && <option value={form.scopeType}>{scopes.find((scope) => scope.key === form.scopeType)?.label ?? SCOPE_LABELS[form.scopeType]}</option>}</select></div>
          {(form.scopeType === 'JURISDICTION' || form.scopeType === 'COMPLIANCE_SERVICE') && (
            <div>
              <label className="block text-xs text-muted mb-1">Jurisdiction <span className="text-rose-400">*</span></label>
              <select value={form.jurisdiction} onChange={(e) => setForm((f) => ({ ...f, jurisdiction: e.target.value, serviceType: '' }))} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent" required>
                <option value="">— select —</option>
                {JURISDICTION_CODES.map((j) => <option key={j} value={j}>{j}</option>)}
              </select>
              <p className="text-xs text-muted mt-1">e.g. UK — determines the available service types.</p>
            </div>
          )}
          {form.scopeType === 'COMPLIANCE_SERVICE' && (
            <div>
              <label className="block text-xs text-muted mb-1">Service type <span className="text-rose-400">*</span></label>
              <select value={form.serviceType} onChange={(e) => updateForm('serviceType', e.target.value)} disabled={!form.jurisdiction} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent disabled:opacity-50" required>
                <option value="">— select —</option>
                {serviceTypes.map((s) => <option key={s} value={s}>{s}</option>)}
                {showLegacyService && <option value={form.serviceType}>{form.serviceType} (current)</option>}
                <option value="Other">Other</option>
              </select>
              {form.jurisdiction && !serviceTypesLoading && serviceTypes.length === 0 ? (
                <p className="text-xs text-amber-300/90 mt-1">No service types for {form.jurisdiction} yet — add one under Admin → Compliance Templates.</p>
              ) : <p className="text-xs text-muted mt-1">Pick a service for the chosen jurisdiction.</p>}
            </div>
          )}
          <div><label className="block text-xs text-muted mb-1">Metric <span className="text-rose-400">*</span></label><select value={form.metric} onChange={(e) => updateForm('metric', e.target.value as GoalOutput['metric'])} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white">{metrics.filter((metric) => metric.scopeType === form.scopeType && metric.enabled && goalMetricAllowed(metric.key, form.scopeType)).sort((a, b) => a.sortOrder - b.sortOrder).map((metric) => <option key={metric.key} value={metric.key}>{metric.label}</option>)}{!metrics.some((metric) => metric.scopeType === form.scopeType && metric.key === form.metric && metric.enabled) && <option value={form.metric}>{metrics.find((metric) => metric.scopeType === form.scopeType && metric.key === form.metric)?.label ?? METRIC_LABELS[form.metric]}</option>}</select></div>
          {form.metric === 'REVENUE' && <div><label className="block text-xs text-muted mb-1">Currency <span className="text-rose-400">*</span></label><select value={form.currency} onChange={(e) => updateForm('currency', e.target.value as Currency)} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white" required>{SUPPORTED_CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>}
          <div><label className="block text-xs text-muted mb-1">Target <span className="text-rose-400">*</span></label><input type="number" min="0.01" step="any" value={form.targetValue} onChange={(e) => updateForm('targetValue', Number(e.target.value))} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white" required /></div>
          {isEdit && form.metric === 'CUSTOM' && <div><label className="block text-xs text-muted mb-1">Current progress</label><input type="number" min="0" step="any" value={form.currentValue} onChange={(e) => updateForm('currentValue', Number(e.target.value))} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white" required /></div>}
          <div><label className="block text-xs text-muted mb-1">Period</label><select value={form.period} onChange={(e) => updateForm('period', e.target.value as GoalOutput['period'])} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white">{Object.entries(PERIOD_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></div>
          <div><label className="block text-xs text-muted mb-1">Start date <span className="text-rose-400">*</span></label><DateInput value={form.startDate} onChange={(e) => updateForm('startDate', e.target.value)} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white" required /></div>
          <div><label className="block text-xs text-muted mb-1">End date <span className="text-rose-400">*</span></label><DateInput value={form.endDate} onChange={(e) => updateForm('endDate', e.target.value)} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white" required /></div>
        </div>
        {!isEdit && canManage && (
          <div className="border border-border rounded-lg p-3 space-y-2">
            <label className="flex items-center gap-2 text-sm text-white"><input type="checkbox" checked={form.firmWide} onChange={(e) => updateForm('firmWide', e.target.checked)} />Count the whole team’s work</label>
            {!form.firmWide && <div><label className="block text-xs text-muted mb-1">Goal owner</label><SearchableSelect value={form.ownerId} onChange={(v) => updateForm('ownerId', v)} placeholder="Select owner" clearable={false} options={users.filter((u) => u.status !== 'DISABLED').map((u) => ({ value: u.id, label: u.displayName }))} /></div>}
          </div>
        )}
        <div><label className="block text-xs text-muted mb-1">Notes</label><textarea value={form.notes} onChange={(e) => updateForm('notes', e.target.value)} className="w-full bg-bg-base border border-border rounded-lg px-3 py-2 text-sm text-white" rows={2} maxLength={2000} /></div>
        {error && <p className="text-xs text-rose-400">{error}</p>}
        <div className="flex justify-end gap-2"><button type="button" onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button><button type="submit" disabled={isPending} className="bg-accent hover:bg-emerald-400 disabled:opacity-50 text-accent-fg text-sm font-medium px-4 py-1.5 rounded-lg">{isPending ? 'Saving…' : isEdit ? 'Save' : 'Create'}</button></div>
      </form>
    </ModalShell>
  );
}

function GoalProgressModal({ goal, scope, metric, onClose }: { goal: GoalOutput; scope: string; metric: string; onClose: () => void }) {
  const progress = useQuery({
    queryKey: ['goal-progress', goal.id],
    queryFn: async () => {
      const res = await fetch(`/v1/goals/${goal.id}/progress`, { headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` } });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: { items: GoalProgressRow[] } };
      return body.data.items;
    },
  });
  return (
    <ModalShell title={`Progress · ${goal.title}`} onClose={onClose}>
      <p className="text-xs text-muted mb-4">{scope} · {metric}</p>
      {progress.isLoading && <p className="py-8 text-center text-sm text-muted">Loading contributing records…</p>}
      {progress.error && <p className="py-4 text-center text-sm text-rose-400">{(progress.error as Error).message}</p>}
      {progress.data?.length === 0 && <p className="py-8 text-center text-sm text-muted">No contributing records in this goal period.</p>}
      <div className="space-y-1">
        {progress.data?.map((row) => (
          <Link key={row.id} to={row.href} onClick={onClose} className="flex items-start gap-3 rounded-lg border border-border/50 px-3 py-2.5 hover:bg-bg-hover/40">
            <div className="min-w-0 flex-1"><div className="text-sm text-white">{row.label}</div>{row.sub && <div className="text-xs text-muted mt-0.5">{row.sub}</div>}{row.date && <div className="text-[11px] text-muted mt-0.5">{fmtDateTime(row.date)}</div>}</div>
            <div className="text-sm text-accent whitespace-nowrap">+{fmtValue(row.value, goal.metric, goal.currency)}</div>
          </Link>
        ))}
      </div>
    </ModalShell>
  );
}

function DeleteModal({ goal, onClose, onConfirm, isPending, error }: { goal: GoalOutput; onClose: () => void; onConfirm: () => void; isPending: boolean; error: string | null }) {
  return <ModalShell title="Delete goal" onClose={onClose}><p className="text-sm text-white">Delete &ldquo;{goal.title}&rdquo;?</p>{error && <p className="text-xs text-rose-400 mt-2">{error}</p>}<div className="flex justify-end gap-2 mt-4"><button type="button" onClick={onClose} className="text-sm text-muted hover:text-white px-3 py-1.5">Cancel</button><button type="button" onClick={onConfirm} disabled={isPending} className="bg-rose-500 hover:bg-rose-400 disabled:opacity-50 text-oncolor text-sm font-medium px-4 py-1.5 rounded-lg">{isPending ? 'Deleting…' : 'Delete'}</button></div></ModalShell>;
}

function ModalShell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"><div className="bg-bg-panel border border-border rounded-xl w-full max-w-2xl mx-4 shadow-2xl animate-pop p-5 max-h-[90vh] overflow-y-auto"><div className="flex items-center justify-between mb-4"><h2 className="text-base font-semibold text-white">{title}</h2><button onClick={onClose} className="text-muted hover:text-white">✕</button></div>{children}</div></div>;
}

function fmtValue(value: number, metric: GoalOutput['metric'], currency?: GoalOutput['currency']) {
  if (metric === 'HOURS_BILLED') return `${Number(value.toFixed(1))}h`;
  if (metric === 'REVENUE') return `${currency ? `${currency} ` : ''}${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}
